import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { leavePoliciesApi, usersApi, holidaysApi } from '../../services/api';
import './AdminLeavePolicies.css';

export default function AdminLeavePoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [users, setUsers] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [isPolicyModalOpen, setPolicyModalOpen] = useState(false);
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  
  // Current edit/assignment focus
  const [currentPolicy, setCurrentPolicy] = useState(null);
  
  // Assign policy state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assignSearchQuery, setAssignSearchQuery] = useState('');
  const [holidaySearchQuery, setHolidaySearchQuery] = useState('');

  // Policy Form state
  const [policyForm, setPolicyForm] = useState({ name: '', description: '', balances: [], holidays: [] });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [polRes, usrRes, holRes] = await Promise.all([
        leavePoliciesApi.list(),
        usersApi.list({ limit: 1000 }), // adjust list method to fetch employees/users
        holidaysApi.list({ limit: 100 })
      ]);
      setPolicies(polRes.data.data || []);
      setUsers(usrRes.data.data || []);
      setHolidays(holRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load leave policies data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setCurrentPolicy(null);
    setPolicyForm({ name: '', description: '', balances: [], holidays: [] });
    setHolidaySearchQuery('');
    setPolicyModalOpen(true);
  };

  const handleOpenEdit = (policy) => {
    setCurrentPolicy(policy);
    setPolicyForm({
      name: policy.name,
      description: policy.description || '',
      balances: policy.balances || [],
      holidays: policy.holidays?.map(h => h.id) || []
    });
    setHolidaySearchQuery('');
    setPolicyModalOpen(true);
  };

  const handleOpenAssign = (policy) => {
    setCurrentPolicy(policy);
    // Find users who already have this policy (if tracked by user.leave_policy_id)
    const currentlyAssigned = users.filter(u => u.leave_policy_id === policy.id).map(u => u.id);
    setSelectedUserIds(currentlyAssigned);
    setAssignSearchQuery('');
    setAssignModalOpen(true);
  };

  const handleSavePolicy = async (e) => {
    e.preventDefault();
    try {
      if (currentPolicy) {
        await leavePoliciesApi.update(currentPolicy.id, policyForm);
        toast.success('Policy updated');
      } else {
        await leavePoliciesApi.create(policyForm);
        toast.success('Policy created');
      }
      setPolicyModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save policy');
    }
  };

  const handleDeletePolicy = async (id) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    try {
      await leavePoliciesApi.delete(id);
      toast.success('Policy deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete policy');
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      await leavePoliciesApi.assign(currentPolicy.id, { userIds: selectedUserIds });
      toast.success('Policy assigned to selected users');
      setAssignModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to assign policy');
    }
  };

  // Form field handlers
  const handleAddBalance = () => {
    setPolicyForm({ ...policyForm, balances: [...policyForm.balances, { leave_type: 'earned', allocated_days: 0 }] });
  };
  const handleRemoveBalance = (index) => {
    const b = [...policyForm.balances]; b.splice(index, 1);
    setPolicyForm({ ...policyForm, balances: b });
  };
  const handleBalanceChange = (index, field, value) => {
    const b = [...policyForm.balances];
    b[index][field] = value;
    setPolicyForm({ ...policyForm, balances: b });
  };

  const toggleHoliday = (holidayId) => {
    const h = [...policyForm.holidays];
    if (h.includes(holidayId)) {
      setPolicyForm({ ...policyForm, holidays: h.filter(id => id !== holidayId) });
    } else {
      setPolicyForm({ ...policyForm, holidays: [...h, holidayId] });
    }
  };

  const toggleUser = (userId) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const filteredPolicies = policies.filter((policy) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;

    const name = String(policy.name || '').toLowerCase();
    const description = String(policy.description || '').toLowerCase();
    const balanceTypes = (policy.balances || []).map((b) => String(b.leave_type || '').toLowerCase()).join(' ');
    const holidayNames = (policy.holidays || []).map((h) => String(h.name || '').toLowerCase()).join(' ');

    return [name, description, balanceTypes, holidayNames].some((text) => text.includes(query));
  });

  const filteredAssignableUsers = users.filter((u) => {
    const query = assignSearchQuery.trim().toLowerCase();
    if (!query) return true;

    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
    const email = String(u.email || '').toLowerCase();
    const designation = String(u.designation || '').toLowerCase();

    return [fullName, email, designation].some((text) => text.includes(query));
  });

  const filteredHolidays = holidays.filter((hol) => {
    const query = holidaySearchQuery.trim().toLowerCase();
    if (!query) return true;

    const name = String(hol.name || '').toLowerCase();
    const date = String(hol.date || '').toLowerCase();
    const country = String(hol.country?.name || '').toLowerCase();
    const region = String(hol.region?.name || '').toLowerCase();
    const type = String(hol.type || '').toLowerCase();

    return [name, date, country, region, type].some((text) => text.includes(query));
  });

  if (loading) {
    return <div style={{ padding: '40px', color: '#fff' }}>Loading policies...</div>;
  }

  return (
    <div className="policies-page">
      <div className="page-header">
        <div>
          <div className="page-title">Manage Leave Policies</div>
          <div className="page-subtitle">Create and assign leave policies to employees</div>
        </div>
        <div className="policy-header-actions">
          <input
            type="text"
            className="form-input policy-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search policy, leave type, holiday..."
          />
          <button className="btn btn-primary" onClick={handleOpenCreate}>
            + Create Policy
          </button>
        </div>
      </div>

      <div className="policies-list">
        {filteredPolicies.length === 0 ? (
          <div className="empty-state">No leave policies found. Create one above.</div>
        ) : (
          filteredPolicies.map(policy => (
            <div key={policy.id} className="card policy-card">
              <div className="policy-card-header">
                <h3>{policy.name}</h3>
                <div className="policy-card-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => handleOpenAssign(policy)}>Assign to Employees</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleOpenEdit(policy)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeletePolicy(policy.id)}>Delete</button>
                </div>
              </div>
              <p>{policy.description}</p>
              
              <div className="policy-details">
                <div className="policy-balances">
                  <h4>Leave Balances</h4>
                  {policy.balances?.length > 0 ? (
                    <ul>
                      {policy.balances.map(b => (
                        <li key={b.id || b.leave_type}>{b.leave_type}: {b.allocated_days} days</li>
                      ))}
                    </ul>
                  ) : <span className="text-muted">No balances set</span>}
                </div>
                <div className="policy-holidays">
                  <h4>Associated Holidays</h4>
                  {policy.holidays?.length > 0 ? (
                    <ul>
                      {policy.holidays.map(h => (
                        <li key={h.id}>{h.name} ({h.date}){h.country?.name ? ` — ${h.country.name}` : h.region?.name ? ` — ${h.region.name}` : ''}</li>
                      ))}
                    </ul>
                  ) : <span className="text-muted">No holidays assigned</span>}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {isPolicyModalOpen && (
        <div className="modal-overlay" onClick={() => { setPolicyModalOpen(false); setHolidaySearchQuery(''); }}>
          <div className="modal policy-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{currentPolicy ? 'Edit Policy' : 'Create Policy'}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => { setPolicyModalOpen(false); setHolidaySearchQuery(''); }}>✕</button>
            </div>
            <form onSubmit={handleSavePolicy}>
              <div className="modal-body policy-modal-body">
                <div className="form-group">
                  <label className="form-label">Policy Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    value={policyForm.name}
                    onChange={e => setPolicyForm({...policyForm, name: e.target.value})}
                    placeholder="e.g. Standard Annual Policy"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    value={policyForm.description}
                    onChange={e => setPolicyForm({...policyForm, description: e.target.value})}
                    rows="3"
                    placeholder="Optional description"
                  />
                </div>

                <div className="form-section">
                  <h4>Leave Balances</h4>
                  {policyForm.balances.map((bal, idx) => (
                    <div key={idx} className="balance-row">
                      <select className="form-select" value={bal.leave_type} onChange={e => handleBalanceChange(idx, 'leave_type', e.target.value)}>
                        <option value="earned">Earned</option>
                        <option value="sick">Sick</option>
                        <option value="casual">Casual</option>
                        <option value="menstrual_leaves">Menstrual Leaves</option>
                        <option value="WFH">WFH</option>
                        <option value="maternity">Maternity</option>
                        <option value="paternity">Paternity</option>
                        <option value="unpaid">Unpaid</option>
                        <option value="birthday">Birthday</option>
                        <option value="Special">Special</option>
                      </select>
                      <input
                        type="number"
                        className="form-input"
                        step="0.5"
                        min="0"
                        required
                        value={bal.allocated_days}
                        onChange={e => handleBalanceChange(idx, 'allocated_days', e.target.value)}
                      />
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveBalance(idx)}>✕</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm mt-2" onClick={handleAddBalance}>+ Add Balance</button>
                </div>

                <div className="form-section">
                  <h4>Select Holidays</h4>
                  <div className="form-group holiday-search-group">
                    <label className="form-label">Search Holiday</label>
                    <input
                      type="text"
                      className="form-input"
                      value={holidaySearchQuery}
                      onChange={(e) => setHolidaySearchQuery(e.target.value)}
                      placeholder="Search by holiday name, date, country, region..."
                    />
                  </div>
                  <div className="policy-holidays-grid">
                    {filteredHolidays.length === 0 ? (
                      <div className="text-muted holiday-empty-state">No holidays match your search.</div>
                    ) : filteredHolidays.map(hol => {
                      const location = hol.country?.name || hol.region?.name || (hol.type ? hol.type : '');
                      return (
                        <label key={hol.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={policyForm.holidays.includes(hol.id)}
                            onChange={() => toggleHoliday(hol.id)}
                          />
                          <span>
                            {hol.name} ({hol.date})
                            {location ? ` — ${location}` : ''}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setPolicyModalOpen(false); setHolidaySearchQuery(''); }}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Policy</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isAssignModalOpen && (
        <div className="modal-overlay" onClick={() => { setAssignModalOpen(false); setAssignSearchQuery(''); }}>
          <div className="modal policy-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assign Policy: {currentPolicy?.name}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => { setAssignModalOpen(false); setAssignSearchQuery(''); }}>✕</button>
            </div>
            <form onSubmit={handleAssignSubmit}>
              <div className="modal-body">
                <div className="form-group assign-search-group">
                  <label className="form-label">Search Employee</label>
                  <input
                    type="text"
                    className="form-input"
                    value={assignSearchQuery}
                    onChange={(e) => setAssignSearchQuery(e.target.value)}
                    placeholder="Search by name, email"
                  />
                </div>
                <div className="user-assign-list">
                  {filteredAssignableUsers.length === 0 ? (
                    <div className="text-muted assign-empty-state">No employees match your search.</div>
                  ) : (
                    filteredAssignableUsers.map(u => (
                      <label key={u.id} className="checkbox-label user-label">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(u.id)}
                          onChange={() => toggleUser(u.id)}
                        />
                        <span>{u.first_name} {u.last_name} ({u.email})</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setAssignModalOpen(false); setAssignSearchQuery(''); }}>Cancel</button>
                <button type="submit" className="btn btn-primary">Assign Selection</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}